var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet/es/Helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { SubmissionError } from 'redux-form';
import { CustomRedirect } from '@/components/functional/Link/CustomRedirect/CustomRedirect';
import { propMatch } from '@/helpers/custom-prop-types';
import { useNavigate } from '@/hooks/lib/react-router/useNavigate';
import { documentListPath, myPagePath } from '@/utils/router/links';
import { API_CREATE_DOCUMENT_FAVORITE, API_DELETE_DOCUMENT_FAVORITE, API_GET_ONE_DOCUMENT, apiCreateDocumentFavorite, apiCreateDocumentRegistration, apiCreateDocumentView, apiDeleteDocumentFavorite, apiGetOneDocument, } from '../../../actions/DocumentActions';
import { SHARE_LEVEL_CLIENT, SHARE_LEVEL_PRO, } from '../../../define/consts/document';
import { REGISTRATION_TYPE_DOCUMENT } from '../../../define/consts/registration';
import { BAD_DIVISION_CLIENT, BAD_DIVISION_PROFESSIONAL, } from '../../../define/response';
import { notify, wrapDispatch } from '../../../helpers/action';
import { isClient, isGuest, isClientPreStatus } from '../../../helpers/auth';
import { isLoading, isLoadingIfUndefined } from '../../../helpers/fetch';
import { removeLineBreaksFromString } from '../../../helpers/string';
import useAnalytics from '../../../helpers/use/analytics/useAnalytics';
import { useRegistrationCta } from '../../../helpers/use/useRegistrationCta';
import { createValidateErrorData } from '../../../helpers/validate';
import { bind as bindAuthKana } from '../../../vendor/AutoKana';
import GoogleAnalytics from '../../../vendor/GoogleAnalytics';
import Layout from '../../Layout';
import Breadcrumb from '../../Layout/Breadcrumb';
import ExaminationAlertAndHeadCardFullModal from '../../Ui/Modal/ExaminationAlertAndHeadCardFullModal';
import RegistrationCompleteModal from '../../Ui/Modal/RegistrationCompleteModal';
import RegistrationCta from '../../Ui/Registration/RegistrationCta';
import DocumentComponent from './DocumentComponent';
import DocumentComponentSkeleton from './DocumentComponentSkeleton';
import FooterFollow from './FooterFollow';
const DocumentOne = (props) => {
    const { match: { params: { id }, }, } = props;
    const history = useNavigate();
    // reducer処理
    const dispatch = useDispatch();
    const { auth, document, favoriteLoading, loading, profile } = useSelector((state) => ({
        auth: state.auth,
        document: state.document.one[id],
        favoriteLoading: isLoadingIfUndefined(state, API_CREATE_DOCUMENT_FAVORITE, id, false) ||
            isLoadingIfUndefined(state, API_DELETE_DOCUMENT_FAVORITE, id, false),
        loading: isLoading(state, API_GET_ONE_DOCUMENT, id),
        profile: state.profile,
    }));
    // ユーザー情報
    const member = useMemo(() => ({
        isClientUser: isClient(auth),
        isGuestUser: isGuest(auth),
        isPreUser: isClientPreStatus(profile),
    }), [auth, profile]);
    const loadingRef = useRef(false);
    const unmountRef = useRef(false);
    const kanaRef = useRef({});
    useEffect(() => () => {
        unmountRef.current = true;
    }, []);
    loadingRef.current = loading;
    // カナ取得
    useEffect(() => {
        if (document && member.isGuestUser) {
            kanaRef.current = {
                first: bindAuthKana('#firstName', ''),
                last: bindAuthKana('#lastName', ''),
            };
        }
        return () => {
            const kana = kanaRef.current;
            if (kana && kana.last) {
                kana.last.removeEvents();
            }
            if (kana && kana.first) {
                kana.first.removeEvents();
            }
        };
    }, [document, member.isGuestUser]);
    // お気に入り取得状態
    const [favorites, changeFavorites] = useState({});
    const favoriteById = favorites[id];
    const documentHasFavorite = !!(document && document.hasFavorite);
    const hasFavorite = useMemo(() => {
        if (favoriteById === undefined) {
            return documentHasFavorite;
        }
        return !!favoriteById;
    }, [documentHasFavorite, favoriteById]);
    // 仮登録モーダル
    const [showPreAlertModal, changeShowPreAlertModal] = useState(false);
    const showPreAlert = useCallback(() => changeShowPreAlertModal(true), []);
    const hidePreAlert = useCallback(() => changeShowPreAlertModal(false), []);
    const onCreateFavorite = useCallback((e) => {
        if (member.isPreUser) {
            showPreAlert();
            return;
        }
        const { value } = e.currentTarget;
        changeFavorites((prevState) => (Object.assign(Object.assign({}, prevState), { [value]: true })));
        wrapDispatch(dispatch, apiCreateDocumentFavorite(value)).then(() => {
            notify(_jsxs(_Fragment, { children: [_jsx("span", { children: "\u30DE\u30A4\u30DA\u30FC\u30B8\u306B\u4FDD\u5B58\u3057\u307E\u3057\u305F" }), _jsx(Link, Object.assign({ to: '/mypage/favorites' }, { children: "\u30DA\u30FC\u30B8\u306B\u79FB\u52D5" }))] }));
        });
    }, [dispatch, showPreAlert, member]);
    const onDeleteFavorite = useCallback((e) => {
        if (member.isPreUser) {
            showPreAlert();
            return;
        }
        const { value } = e.currentTarget;
        changeFavorites((prevState) => (Object.assign(Object.assign({}, prevState), { [value]: false })));
        dispatch(apiDeleteDocumentFavorite(value));
    }, [dispatch, showPreAlert, member]);
    // データ取得
    useEffect(() => {
        if (loadingRef.current === true) {
            return;
        }
        wrapDispatch(dispatch, apiGetOneDocument(id)).then(() => {
            dispatch(apiCreateDocumentView(id));
        });
    }, [id, loadingRef, dispatch, member.isClientUser]);
    // ナビゲーション
    const pans = useMemo(() => {
        const memoPans = [
            {
                link: documentListPath(),
                text: 'ダウンロード資料',
            },
        ];
        if (document) {
            memoPans.push({ link: '', text: document.title });
        }
        if (!member.isGuestUser) {
            memoPans.unshift({
                link: myPagePath(),
                text: 'マイページ',
            });
        }
        return memoPans;
    }, [member.isGuestUser, document]);
    // submit後のイベント処理
    const onSubmit = useCallback((value) => __awaiter(void 0, void 0, void 0, function* () {
        GoogleAnalytics.sendClickEvent('column', 'column_registration_click');
        const kana = kanaRef.current || {};
        const lastNameKana = kana.last ? kana.last.getFurigana() : '';
        const firstNameKana = kana.last ? kana.first.getFurigana() : '';
        const data = Object.assign(Object.assign({}, value), { firstNameKana,
            lastNameKana });
        let res;
        try {
            res = yield wrapDispatch(dispatch, apiCreateDocumentRegistration(id, data));
        }
        catch (e) {
            const { shareLevel } = document;
            const isClientLimit = shareLevel === SHARE_LEVEL_CLIENT;
            const isProLimit = shareLevel === SHARE_LEVEL_PRO;
            const clientMessage = isClientLimit
                ? 'コンサルタントの方はダウンロードできません'
                : 'コンサルタントの方は「コンサルファーム」をご選択ください';
            const proMessage = isProLimit
                ? 'クライアントの方はダウンロードできません'
                : 'クライアントの方は「企業」をご選択ください';
            const errorMessage = createValidateErrorData(e, {
                [BAD_DIVISION_CLIENT]: { key: 'email', message: clientMessage },
                [BAD_DIVISION_PROFESSIONAL]: { key: 'email', message: proMessage },
            });
            throw new SubmissionError(errorMessage);
        }
        const { payload: { code }, } = res;
        if (!unmountRef.current) {
            history.push(`/document_download/${code}`);
        }
    }), [history, id, dispatch, unmountRef, document]);
    const { onClickRegistration } = useAnalytics.useColumn();
    const { hideModal, isShowForm, isShowModal, onSubmitAfter } = useRegistrationCta.useRegistrationCtaForm('column_registration_form', member.isGuestUser, onClickRegistration);
    const metaDescription = useMemo(() => document ? removeLineBreaksFromString(document.metaDescription) : '', [document]);
    if (loadingRef.current === false && !document) {
        return _jsx(CustomRedirect, { to: documentListPath() });
    }
    return (_jsxs(Layout, Object.assign({ mainClassName: 'l-main document_one_container column_common_container no-need-bottom-space' }, { children: [_jsxs(Helmet, { children: [_jsx("title", { children: `${(document === null || document === void 0 ? void 0 : document.title) || 'ダウンロード資料'}｜PROFFIT` }), metaDescription && (_jsx("meta", { content: metaDescription, name: 'description' }))] }), _jsx(Breadcrumb, { lists: pans }), !document ? (_jsx(DocumentComponentSkeleton, {})) : (_jsx(DocumentComponent, { document: document, favoriteLoading: favoriteLoading, hasFavorite: hasFavorite, isClientUser: member.isClientUser, isGuestUser: member.isGuestUser, onCreateFavorite: onCreateFavorite, onDeleteFavorite: onDeleteFavorite, onSubmit: onSubmit })), document && _jsx(FooterFollow, { id: id }), _jsx(RegistrationCta, { isShow: isShowForm, onSubmitAfter: onSubmitAfter, registrationType: REGISTRATION_TYPE_DOCUMENT, scrollName: 'column_registration_form' }), _jsx(RegistrationCompleteModal, { hideModal: hideModal, isShow: isShowModal, timeout: 300 }), _jsx(ExaminationAlertAndHeadCardFullModal, { isShow: showPreAlertModal, onClickClose: hidePreAlert })] })));
};
DocumentOne.propTypes = {
    match: propMatch(),
};
export default DocumentOne;
